import { PlatformError, type DocStorePort, type DocTxOps, type DocTxRunner } from '@djgg/core';
import {
  assetGenJobDoc,
  buildJobDoc,
  creationDoc,
  qualityOutboxCollection,
  qualityOutboxDoc,
  qualityReservationDoc,
  qualityReservationsCollection,
  qualityRunDoc,
  qualityTransitionDoc,
  versionDoc,
  failedCandidateDoc,
  candidateEligibilityDoc,
} from '../paths.js';
import { assertQualityRunClaim } from '../qualityClaimAuthority.js';
import {
  evaluatePreviewEligibility,
  failedCandidateIdentity,
  validateFailedCandidateV1,
  type FailedCandidateV1,
} from '../failedCandidate.js';
import { canonicalJson } from '@djgg/gameplay-kit';
import { qualityTransitionEventDigest, reduceQualityTransition, type QualityTransitionRecordV1 } from '../qualityStateMachine.js';
import type { QualityClaimV1, ProviderReservationV1 } from '../qualitySchemas.js';
import type { AssetGenJob, BuildJob, Creation, CreationVersion } from '../types.js';
import type { QualityOutboxRecordV1 } from './qualityOutbox.js';
import type { QualityRunRecordV2 } from './qualityRuns.js';
import { STALE_HEARTBEAT_MS } from './buildJobs.js';
import { healTerminalBuild } from './buildTerminalHeal.js';
import type { StoredFailedCandidateV1 } from './candidateInspection.js';

export interface QualityLifecycleIdentityV2 {
  jobId: string;
  creationId: string;
  version: number;
}

export interface QualityExpiredClaimV2 extends QualityLifecycleIdentityV2 {
  claimToken: string;
  leaseGeneration: number;
}

function reasonText(value: string): string {
  const normalized = value.trim().slice(0, 256);
  if (!normalized) throw new PlatformError('validation', 'quality lifecycle reason is required');
  return normalized;
}

function lifecycleClaim(jobId: string, job: BuildJob, run: QualityRunRecordV2, now: number): QualityClaimV1 {
  return {
    jobId,
    claimToken: job.claimToken ?? 'lifecycle-command',
    leaseGeneration: Math.max(1, job.leaseGeneration ?? 0),
    leaseExpiry: Math.max(1, job.leaseExpiry ?? now),
    expectedRunRevision: run.state.revision,
  };
}

/** One transactional authority for verified-core@2 claim use, completion,
 * cancellation, terminal failure, and expired-claim recovery. Callers must
 * never mutate one member of the admitted job/run/version tuple separately. */
export class QualityLifecycleRepo {
  constructor(
    private readonly store: DocStorePort & DocTxRunner,
    private readonly now: () => number = () => Date.now(),
    private readonly leaseMs = STALE_HEARTBEAT_MS,
  ) {}

  /** Return the exact active claim for an admitted run. The transaction
   * re-reads and validates the whole job/version/run tuple, so adapter startup
   * cannot manufacture authority from a stale job object. */
  async claim(jobId: string, expectedRunRevision: number): Promise<QualityClaimV1> {
    return this.store.runTransaction(async tx => {
      const { job, run } = await this.loadTuple(tx, jobId);
      const claim = lifecycleClaim(jobId, job, run, this.now());
      claim.expectedRunRevision = expectedRunRevision;
      return assertQualityRunClaim(tx, run, claim, this.now());
    });
  }

  /** Renew a v2 claim while holding the admitted run and build-job locks. */
  async heartbeat(jobId: string, claimValue: QualityClaimV1): Promise<QualityClaimV1> {
    const now = this.now();
    return this.store.runTransaction(async tx => {
      const { job, run } = await this.loadTuple(tx, jobId);
      const claim = await assertQualityRunClaim(tx, run, claimValue, now);
      const leaseExpiry = now + this.leaseMs;
      await tx.update(buildJobDoc(jobId), {
        heartbeatAt: this.store.serverTimestamp(),
        leaseExpiry,
      });
      return { ...claim, leaseExpiry };
    });
  }

  /** Complete only a published v2 run under its exact active generation. */
  async complete(jobId: string, claimValue: QualityClaimV1): Promise<void> {
    const now = this.now();
    await this.store.runTransaction(async tx => {
      const { job, run } = await this.loadTuple(tx, jobId);
      if (run.state.phase !== 'published') {
        throw new PlatformError('conflict', 'quality lifecycle completion requires a published run');
      }
      if (job.status === 'done') {
        if (job.claimToken !== claimValue.claimToken
          || job.leaseGeneration !== claimValue.leaseGeneration
          || claimValue.jobId !== jobId
          || claimValue.expectedRunRevision !== run.state.revision) {
          throw new PlatformError('conflict', 'completed quality job does not match the supplied claim');
        }
        return;
      }
      await assertQualityRunClaim(tx, run, claimValue, now);
      await tx.update(buildJobDoc(jobId), {
        status: 'done', error: null, finishedAt: this.store.serverTimestamp(), leaseExpiry: null,
      });
    });
  }

  async cancel(input: QualityLifecycleIdentityV2, reasonValue: string): Promise<void> {
    await this.terminate(input, 'cancelled', reasonValue);
  }

  async fail(input: QualityLifecycleIdentityV2, reasonValue: string): Promise<void> {
    await this.terminate(input, 'failed', reasonValue);
  }

  async cancelClaim(jobId: string, claim: QualityClaimV1, reasonValue: string): Promise<void> {
    await this.terminateClaim(jobId, claim, 'cancelled', reasonValue);
  }

  async failClaim(
    jobId: string,
    claim: QualityClaimV1,
    reasonValue: string,
    candidateValue?: FailedCandidateV1 | null,
  ): Promise<void> {
    await this.terminateClaim(jobId, claim, 'failed', reasonValue, candidateValue);
  }

  private async terminateClaim(
    jobId: string,
    claim: QualityClaimV1,
    outcome: 'cancelled' | 'failed',
    reasonValue: string,
    candidateValue?: FailedCandidateV1 | null,
  ): Promise<void> {
    const job = await this.store.getDoc<BuildJob>(buildJobDoc(jobId));
    if (!job) throw new PlatformError('not-found', `quality lifecycle job ${jobId} not found`);
    await this.terminate(
      { jobId, creationId: job.creationId, version: job.version },
      outcome,
      reasonValue,
      claim,
      candidateValue,
    );
  }

  private async terminate(
    input: QualityLifecycleIdentityV2,
    outcome: 'cancelled' | 'failed',
    reasonValue: string,
    claimValue?: QualityClaimV1,
    candidateValue?: FailedCandidateV1 | null,
  ): Promise<void> {
    const reason = reasonText(reasonValue);
    const now = this.now();
    const candidate = candidateValue ? validateFailedCandidateV1(candidateValue) : null;
    const candidateDigest = candidate ? await failedCandidateIdentity(candidate) : null;
    await this.store.runTransaction(async tx => {
      if (!tx.query) throw new PlatformError('unavailable', 'quality lifecycle requires transactional queries');
      const jobPath = buildJobDoc(input.jobId);
      const versionPath = versionDoc(input.creationId, input.version);
      const job = await tx.get<BuildJob>(jobPath);
      const version = await tx.get<CreationVersion>(versionPath);
      const creation = await tx.get<Creation>(creationDoc(input.creationId));
      if (!job || !version || !creation) throw new PlatformError('not-found', 'quality lifecycle tuple is incomplete');
      if (job.requiredWorkerCapability !== 'verified-core-v2'
        || job.creationId !== input.creationId || job.version !== input.version
        || !version.qualityRunId) {
        throw new PlatformError('conflict', 'quality lifecycle tuple does not identify verified-core@2 authority');
      }
      const runPath = qualityRunDoc(version.qualityRunId);
      const run = await tx.get<QualityRunRecordV2>(runPath);
      if (!run || run.jobId !== input.jobId || run.creationId !== input.creationId
        || run.version !== input.version || run.runId !== version.qualityRunId) {
        throw new PlatformError('conflict', 'quality lifecycle run binding is invalid');
      }
      if (claimValue) await assertQualityRunClaim(tx, run, claimValue, now);
      if (candidate) {
        if (outcome !== 'failed' || !claimValue || candidate.creationId !== run.creationId
          || candidate.version !== run.version || candidate.claimId !== claimValue.claimToken) {
          throw new PlatformError('conflict', 'failed candidate is not owned by this terminal claim');
        }
        const path = failedCandidateDoc(candidate.candidateId);
        const existing = await tx.get<StoredFailedCandidateV1>(path);
        const proposed: StoredFailedCandidateV1 = {
          candidateId: candidate.candidateId,
          runId: run.runId,
          identityDigest: candidateDigest!,
          candidate,
          attachedAt: existing?.attachedAt ?? now,
        };
        if (existing) {
          const { id: _id, ...stored } = existing;
          if (canonicalJson(stored) !== canonicalJson(proposed)) {
            throw new PlatformError('conflict', 'failed candidate is create-only');
          }
        } else {
          await tx.set(path, proposed as unknown as Record<string, unknown>);
        }
        const eligibilityPath = candidateEligibilityDoc(candidate.candidateId, 'retained');
        const retainedEligibility = await tx.get<ReturnType<typeof evaluatePreviewEligibility>>(eligibilityPath);
        const eligibility = evaluatePreviewEligibility(candidate, null, retainedEligibility?.assessedAt ?? now);
        if (retainedEligibility) {
          const { id: _id, ...stored } = retainedEligibility;
          if (canonicalJson(stored) !== canonicalJson(eligibility)) {
            throw new PlatformError('conflict', 'failed candidate eligibility is append-only');
          }
        } else {
          await tx.set(eligibilityPath, eligibility as unknown as Record<string, unknown>);
        }
      }
      if (run.state.phase === 'published') throw new PlatformError('conflict', 'published quality work cannot be terminated');
      if (run.state.phase !== outcome) {
        const classifiedFailure = outcome === 'failed'
          && (run.state.phase === 'needs-design-clarification' || run.state.phase === 'verification-unsupported');
        if (run.state.phase === 'failed' || run.state.phase === 'cancelled'
          || (!classifiedFailure && (run.state.phase === 'needs-design-clarification'
            || run.state.phase === 'verification-unsupported'))) {
          throw new PlatformError('conflict', `terminal quality phase ${run.state.phase} cannot become ${outcome}`);
        }
        if (!classifiedFailure) {
          const event = { id: `${run.runId}:${outcome}`, type: outcome, reason } as const;
          const next = reduceQualityTransition(run.state, event);
          const claim = claimValue ?? lifecycleClaim(input.jobId, job, run, now);
          const transition: QualityTransitionRecordV1 = {
            runId: run.runId, eventId: event.id, eventDigest: qualityTransitionEventDigest(event),
            fromRevision: run.state.revision, toRevision: next.revision,
            fromPhase: run.state.phase, toPhase: next.phase, claim, committedAt: now,
          };
          await tx.update(runPath, { state: next, updatedAt: now });
          await tx.set(qualityTransitionDoc(run.runId, event.id), transition as unknown as Record<string, unknown>);
        }
      }

      const outboxes = await tx.query<QualityOutboxRecordV1>(qualityOutboxCollection(), { where: [['runId', '==', run.runId]] });
      for (const listed of outboxes) {
        const row = await tx.get<QualityOutboxRecordV1>(qualityOutboxDoc(listed.id));
        if (row && (row.status === 'pending' || row.status === 'claimed')) {
          await tx.update(qualityOutboxDoc(listed.id), {
            status: outcome === 'cancelled' ? 'cancelled' : 'failed',
            error: reason, claimToken: null, leaseExpiry: null,
            finishedAt: this.store.serverTimestamp(),
          });
        }
      }

      const reservations = await tx.query<ProviderReservationV1>(qualityReservationsCollection(), { where: [['runId', '==', run.runId]] });
      for (const listed of reservations) {
        const row = await tx.get<ProviderReservationV1>(qualityReservationDoc(listed.id));
        if (row?.state === 'reserved') {
          await tx.update(qualityReservationDoc(listed.id), {
            state: 'cancelled', cancelledAt: now,
          });
        }
      }

      for (const mediaId of [version.creationRun?.visualJobId, version.creationRun?.musicJobId]) {
        if (!mediaId) continue;
        const media = await tx.get<AssetGenJob>(assetGenJobDoc(mediaId));
        if (media && (media.status === 'awaiting-core' || media.status === 'queued' || media.status === 'running')) {
          await tx.update(assetGenJobDoc(mediaId), {
            status: 'failed', workerId: null, heartbeatAt: null, error: reason,
            failureKind: outcome === 'cancelled' ? 'cancelled' : 'worker',
            finishedAt: this.store.serverTimestamp(),
          });
        }
      }

      const ts = this.store.serverTimestamp();
      if (job.status !== 'done' && job.status !== 'failed') {
        await tx.update(jobPath, {
          status: 'failed', workerId: null, claimToken: null, leaseExpiry: null,
          heartbeatAt: null, error: reason, finishedAt: ts,
        });
      }
      await healTerminalBuild(tx, { creationId: input.creationId, version: input.version,
        creation, versionRow: version, notes: reason, ts });
    });
  }

  async recoverExpired(input: QualityExpiredClaimV2): Promise<void> {
    const now = this.now();
    await this.store.runTransaction(async tx => {
      if (!tx.query) throw new PlatformError('unavailable', 'quality recovery requires transactional queries');
      const jobPath = buildJobDoc(input.jobId);
      const job = await tx.get<BuildJob>(jobPath);
      const version = await tx.get<CreationVersion>(versionDoc(input.creationId, input.version));
      if (!job || !version?.qualityRunId || job.requiredWorkerCapability !== 'verified-core-v2'
        || job.creationId !== input.creationId || job.version !== input.version) {
        throw new PlatformError('conflict', 'quality recovery tuple is invalid');
      }
      if (job.status !== 'claimed' || job.claimToken !== input.claimToken
        || job.leaseGeneration !== input.leaseGeneration || (job.leaseExpiry ?? Number.MAX_SAFE_INTEGER) > now) {
        throw new PlatformError('conflict', 'quality recovery claim changed or is not expired');
      }
      const runPath = qualityRunDoc(version.qualityRunId);
      const run = await tx.get<QualityRunRecordV2>(runPath);
      if (!run || run.jobId !== input.jobId || run.creationId !== input.creationId || run.version !== input.version) {
        throw new PlatformError('conflict', 'quality recovery run binding is invalid');
      }
      if (run.state.phase === 'published') {
        await tx.update(jobPath, {
          status: 'done', error: null, finishedAt: this.store.serverTimestamp(), leaseExpiry: null,
        });
        return;
      }
      const event = { id: `${run.runId}:retry:${run.state.operationAttempt + 1}`, type: 'operation-retry' as const };
      const next = reduceQualityTransition(run.state, event);
      const claim = lifecycleClaim(input.jobId, job, run, now);
      const transition: QualityTransitionRecordV1 = {
        runId: run.runId, eventId: event.id, eventDigest: qualityTransitionEventDigest(event),
        fromRevision: run.state.revision, toRevision: next.revision,
        fromPhase: run.state.phase, toPhase: next.phase, claim, committedAt: now,
      };
      await tx.update(runPath, { state: next, updatedAt: now });
      await tx.set(qualityTransitionDoc(run.runId, event.id), transition as unknown as Record<string, unknown>);

      const outboxes = await tx.query<QualityOutboxRecordV1>(qualityOutboxCollection(), { where: [['runId', '==', run.runId]] });
      for (const listed of outboxes) {
        const row = await tx.get<QualityOutboxRecordV1>(qualityOutboxDoc(listed.id));
        if (row?.status === 'claimed' && (row.leaseExpiry ?? Number.MAX_SAFE_INTEGER) <= now) {
          await tx.update(qualityOutboxDoc(listed.id), {
            status: 'pending', claimToken: null, leaseExpiry: null, availableAt: now,
          });
        }
      }
      const queueEnteredAt = this.store.serverTimestamp();
      await tx.update(jobPath, {
        status: 'queued', workerId: null, claimToken: null, leaseExpiry: null,
        heartbeatAt: null, claimedAt: null, queueEnteredAt, finishedAt: null,
        error: null, attempts: (job.attempts ?? 0) + 1,
      });
      const queueIntervals = [
        ...(job.queueIntervals ?? []).map(interval => ({ ...interval })),
        { enteredAt: now, claimedAt: null },
      ];
      await tx.update(versionDoc(input.creationId, input.version), {
        buildTiming: {
          acceptedAt: version.buildTiming?.acceptedAt ?? version.createdAt,
          workerStartedAt: version.buildTiming?.workerStartedAt ?? null,
          readyAt: null, queueIntervals, queueMs: job.queueMs ?? version.buildTiming?.queueMs ?? 0,
          buildDurationMs: null, totalElapsedMs: null,
        },
      });
      await tx.update(creationDoc(input.creationId), {
        buildState: 'building', updatedAt: this.store.serverTimestamp(),
      });
    });
  }

  private async loadTuple(
    tx: DocTxOps,
    jobId: string,
  ): Promise<{ job: BuildJob & { id: string }; run: QualityRunRecordV2 & { id: string } }> {
    const job = await tx.get<BuildJob>(buildJobDoc(jobId));
    if (!job || job.requiredWorkerCapability !== 'verified-core-v2') {
      throw new PlatformError('conflict', 'quality lifecycle requires a verified-core@2 build job');
    }
    const version = await tx.get<CreationVersion>(versionDoc(job.creationId, job.version));
    if (!version?.qualityRunId) throw new PlatformError('conflict', 'quality lifecycle version has no admitted run');
    const run = await tx.get<QualityRunRecordV2>(qualityRunDoc(version.qualityRunId));
    if (!run || run.jobId !== jobId || run.runId !== version.qualityRunId
      || run.creationId !== job.creationId || run.version !== job.version) {
      throw new PlatformError('conflict', 'quality lifecycle tuple binding is invalid');
    }
    return { job, run };
  }
}
